import 'yet-another-react-lightbox/styles.css'

import { ChatIcon, ChevronDownIcon, ChevronUpIcon, ThreeDotHorizontalIcon } from '@calizahq/react-components-v2'
import { StyledEditor } from '@shared/components/Editor/Editor.styles'
import { getRichTextHTMLGenerator } from '@shared/components/Editor/useRichTextHTMLGenerator'
import clsx from 'clsx'
import { formatInTimeZone } from 'date-fns-tz'
import parse from 'html-react-parser'
import React, { FC, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Lightbox from 'yet-another-react-lightbox'

import { LegacyDropdown } from '@/components'
import { useAlertContext } from '@/context'
import { useToggle } from '@/hooks'
import { ChildComment, TicketComment } from '@/types/TicketComment'

import { ReactionList } from './ReactionList'

export type CommentType = 'Parent' | 'Child'

type Props = {
  activeCommentToReply: number | undefined
  data: TicketComment | ChildComment
  isReadOnly?: boolean
  rightAllignment?: boolean
  scrolledComment?: boolean
  setActiveCommentToReply: (commentId?: number) => void
  supportedReactions?: string[] | null
  type?: CommentType
}
export const Comment: FC<Props> = ({
  data,
  type = 'Parent',
  isReadOnly = false,
  scrolledComment = false,
  setActiveCommentToReply,
  activeCommentToReply,
  rightAllignment,
  supportedReactions,
}) => {
  const location = useLocation()
  const { setAlert } = useAlertContext()
  const [dropdownExpanded, setDropdownExpanded] = useState(false)

  const [richText, setRichText] = useState()
  const { isShown, toggle } = useToggle()
  const { creator, internalOnly, createdAt, text, attachments, externalCreatedByName, externalCreatedByRole } = data

  // Prefer external creator info when provided, otherwise fall back to internal user
  const displayName = externalCreatedByName || creator.fullName || 'Unknown'
  const normalizedRole = externalCreatedByRole
    ? externalCreatedByRole.charAt(0).toUpperCase() + externalCreatedByRole.slice(1).toLowerCase()
    : ''
  const roleDisplay = externalCreatedByName && normalizedRole ? ` (${normalizedRole})` : ''
  const [photoIndex, setPhotoIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const { pathname } = location

  const images =
    attachments && attachments.length
      ? attachments.filter((attachment) => attachment.contentType.includes('image'))
      : []
  const files =
    attachments && attachments.length
      ? attachments.filter((attachment) => !attachment.contentType.includes('image'))
      : []

  const showImage = (index: number) => {
    setPhotoIndex(index)
    setIsOpen(true)
  }

  const getFileName = (fileName: string = '') => {
    const paths = fileName.split('/')
    return paths.pop()
  }

  //Check if user is an applicant(0), member(1), or professional_housing_coordinator(19)
  const externalUsers = [0, 1, 19]

  // Priority: Use externalCreatedByRole if it exists, otherwise fall back to creator.role
  const isHost = externalCreatedByRole === 'HOST' || externalCreatedByRole === 'COHOST'
  const isExternalGuest = externalCreatedByRole && !['HOST', 'COHOST'].includes(externalCreatedByRole)
  const isInternalUser = !externalCreatedByRole && !externalUsers.includes(creator.role)

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const formatedTime = createdAt ? formatInTimeZone(createdAt, timezone, 'MMM dd,yyyy hh:mm aa (z)') : ''

  const textStyled = 'flex w-full flex-col rounded-xl p-4 text-p-sm md:text-base'
  const containerStyled = 'mb-2 flex flex-col'

  React.useEffect(() => {
    getRichTextHTMLGenerator((val) => {
      setRichText(val)
    }, data.richText)
  }, [data.richText])

  const handleCommentClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (activeCommentToReply && activeCommentToReply !== data?.id) {
      setActiveCommentToReply(undefined)
    }
  }

  const activateReplyToComment = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDropdownExpanded(false)
    setActiveCommentToReply(data.id)
  }

  const copySharedLink = () => {
    const url = `${window?.location?.origin}${pathname}#${data.id}`
    navigator.clipboard.writeText(url)
    setAlert('Shared link copied', 'success')
  }
  return (
    <div className={containerStyled} onClick={handleCommentClick} role="presentation">
      <div className={clsx('flex w-full min-w-0 items-center px-2', rightAllignment ? 'flex-row-reverse' : '')}>
        {type !== 'Child' && !isReadOnly && (
          <LegacyDropdown
            className="md:my-0"
            direction={rightAllignment ? 'left' : 'right'}
            isOpen={dropdownExpanded}
            renderToggle={() => {
              return (
                <button>
                  <ThreeDotHorizontalIcon className="pointer" width={16} />
                </button>
              )
            }}
            setIsOpen={setDropdownExpanded}
            size="small"
          >
            <button className="p-2 text-sm md:p-3 md:text-base" onClick={activateReplyToComment}>
              Reply in Thread
            </button>
            <button className="p-2 text-sm md:p-3 md:text-base" onClick={copySharedLink}>
              Share Link
            </button>
          </LegacyDropdown>
        )}
        <p className=" mr-1  text-p-xs  text-tertiary">{formatedTime}</p>
        <p className="mr-1 min-w-0 truncate text-p-sm">
          {displayName}
          {roleDisplay}
        </p>
      </div>

      <div
        className={clsx(
          'relative',
          textStyled,
          // Color logic:
          // - External guests (members) → dark green (avocado), left aligned
          // - Internal comments (team notes) → grey (default), right aligned
          // - Our replies to members → teal (accent-landing), right aligned
          isExternalGuest
            ? 'bg-bg-accent-avocado text-white'
            : isHost || (isInternalUser && !internalOnly)
              ? 'bg-accent-landing text-white'
              : 'bg-emphasis',
          activeCommentToReply === data.id && ' border-bg-page-warning border-2',
          scrolledComment && activeCommentToReply !== data.id && ' border-action-input border-2',
        )}
        style={{ overflowWrap: 'break-word' }}
      >
        {!internalOnly && (
          <div className="mb-1 flex items-center">
            <ChatIcon className="text-grey-400" width={16} />
            <p className="ml-2 text-p-sm text-grey-400">External message</p>
          </div>
        )}
        <>{richText ? <StyledEditor> {parse(richText)} </StyledEditor> : text}</>

        {attachments && attachments.length && (
          <>
            <div className="mt-2 flex items-center">
              {isShown ? (
                <ChevronUpIcon className=" mr-2 icon-primary" onClick={toggle} width={14} />
              ) : (
                <ChevronDownIcon className=" mr-2 icon-primary" onClick={toggle} width={14} />
              )}
              <p className="cursor-pointer text-p-xs text-tertiary" onClick={toggle} role={'presentation'}>
                {' '}
                Attachments
              </p>
            </div>
            {isShown && (
              <div className="mt-2 flex w-full flex-col">
                {files.length > 0 && (
                  <div className="flex flex-wrap">
                    {files.map((file) => (
                      <a
                        className="mb-1 mr-2 rounded-lg bg-white px-2 py-1 text-p-xs underline"
                        href={`/${file.path}`}
                        key={file.id}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {getFileName(file.filename)}
                      </a>
                    ))}
                  </div>
                )}
                {images.length > 0 && (
                  <div className="grid grid-cols-3 place-items-stretch gap-3 md:grid-cols-4">
                    {images.map((file, i) => {
                      return (
                        <img
                          alt={file.filename}
                          aria-hidden="true"
                          className="h-30 w-30 overflow-hidden rounded-lg md:w-48"
                          key={i}
                          onClick={() => showImage(i)}
                          src={`/${file.path}`}
                        />
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Emoji reactions display */}
        {data.reactions && data.reactions.length > 0 && (
          <ReactionList
            className="mt-3 border-t border-white/20 pt-2"
            reactions={data.reactions}
            supportedReactions={supportedReactions}
          />
        )}

        <div>
          <Lightbox
            close={() => setIsOpen(false)}
            index={photoIndex}
            open={isOpen}
            slides={images.map((img) => ({ src: `/${img.path}` }))}
          />
        </div>
      </div>
    </div>
  )
}
